// Local day and time in a named time zone as a moment, and back. Pure Intl, no clock. Used by the plan calendar
// (events with their own time zone) and the push Worker (reminders at the planned local time).
import type { Day } from './dates';

/** Whether the runtime knows this IANA time zone ("Europe/Zurich"). */
export function validTimeZone(tz: unknown): tz is string {
  if (typeof tz !== 'string' || tz.length === 0 || tz.length > 64) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

const offsetFormatters = new Map<string, Intl.DateTimeFormat>();

/** The time zone's offset from UTC at a moment, in ms (Zurich in summer: +2 h). */
export function tzOffset(ms: number, timeZone: string): number {
  let f = offsetFormatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric',
    });
    offsetFormatters.set(timeZone, f);
  }
  const p: Record<string, number> = {};
  for (const part of f.formatToParts(new Date(ms))) if (part.type !== 'literal') p[part.type] = Number(part.value);
  const asUtc = Date.UTC(p.year!, p.month! - 1, p.day!, p.hour!, p.minute!, p.second!);
  return asUtc - (ms - (ms % 1000));
}

/**
 * The moment of a local day and minute in a time zone. Summer time: a time that does not exist (02:30 on the day
 * clocks jump forward) moves forward by the jump (03:30); a time that happens twice (02:30 when clocks go back)
 * is the first one.
 */
export function zonedTime(day: Day, minute: number, timeZone: string): number {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const wall = Date.UTC(y, m - 1, d, Math.floor(minute / 60), minute % 60);
  // Try the offsets just before and after; the earliest candidate whose wall time matches wins.
  const offsets = [...new Set([tzOffset(wall - 86_400_000, timeZone), tzOffset(wall, timeZone), tzOffset(wall + 86_400_000, timeZone)])];
  const matches = offsets.map((o) => wall - o).filter((t) => t + tzOffset(t, timeZone) === wall).sort((a, b) => a - b);
  if (matches.length > 0) return matches[0]!;
  // In the gap: the offset before the jump, which lands after it (02:30 at +1 h is 03:30 at +2 h).
  return wall - tzOffset(wall - 86_400_000, timeZone);
}
